import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Zap, Shield, Check } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState({ next: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (pw.next.length < 6) { toast.error("Mindestens 6 Zeichen"); return; }
    if (pw.next !== pw.confirm) { toast.error("Passwörter stimmen nicht überein"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw.next });
      setDone(true);
      toast.success("Passwort erfolgreich zurückgesetzt");
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Link ungültig oder abgelaufen");
    } finally { setLoading(false); }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md p-8 rounded-3xl glass">
          <h2 className="font-heading text-2xl font-bold mb-4">Ungültiger Link</h2>
          <p className="text-muted-foreground mb-4">Der Reset-Link ist ungültig. Bitte fordere einen neuen an.</p>
          <Link to="/forgot-password" className="text-primary font-semibold">Neuen Link anfordern</Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background bg-grain p-6">
      <Card className="w-full max-w-md p-8 rounded-3xl border-border/60 glass" data-testid="reset-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><Zap className="w-5 h-5" /></div>
          <div className="font-heading text-xl font-bold">Navero</div>
        </div>
        {done ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 mx-auto rounded-3xl bg-accent/15 text-accent flex items-center justify-center mb-4">
              <Check className="w-8 h-8" />
            </div>
            <h2 className="font-heading text-2xl font-bold mb-2">Passwort geändert</h2>
            <p className="text-sm text-muted-foreground">Du wirst automatisch zum Login weitergeleitet...</p>
          </div>
        ) : (
          <>
            <h2 className="font-heading text-2xl font-bold mb-2">Neues Passwort festlegen</h2>
            <p className="text-sm text-muted-foreground mb-6">Wähle ein sicheres Passwort mit mindestens 6 Zeichen.</p>
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div>
                <Label>Neues Passwort</Label>
                <Input type="password" value={pw.next} onChange={e=>setPw({...pw, next:e.target.value})} required minLength={6}
                       className="rounded-full h-11 px-5 mt-1" data-testid="reset-new" />
              </div>
              <div>
                <Label>Passwort bestätigen</Label>
                <Input type="password" value={pw.confirm} onChange={e=>setPw({...pw, confirm:e.target.value})} required
                       className="rounded-full h-11 px-5 mt-1" data-testid="reset-confirm" />
                {pw.next && pw.confirm && pw.next !== pw.confirm && (
                  <p className="text-xs text-destructive mt-1">Passwörter stimmen nicht überein</p>
                )}
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full h-11 font-semibold" data-testid="reset-submit">
                <Shield className="w-4 h-4 mr-2" /> {loading ? "Speichern..." : "Passwort zurücksetzen"}
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
